<template>
  <v-dialog v-model="showDialog" max-width="800px" persistent>
    <v-card class="p-5">
      <div class="text-2xl mb-8" data-testid="add-supervisor-org-dialog-title">
        {{ t("addSupervisorOrgDialog.title") }}
      </div>
      <v-form
        ref="form"
        :disabled="loading"
        :loading="loading"
        validate-on="invalid-input lazy"
        @submit.prevent="onSubmit"
      >
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model.trim="name"
              :rules="[required, minLength(3)]"
              clearable
              :label="t('addSupervisorOrgDialog.name')"
              hide-details="auto"
              variant="outlined"
              data-testid="add-supervisor-org-dialog-name-input"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <ProAccountPriceOptionsSelect v-model="proAccountPriceOptions" />
          </v-col>

          <v-col cols="12">
            <div class="mb-3">{{ t("addSupervisorOrgDialog.createAccountOptional") }}</div>
            <v-text-field
              v-model.trim="email"
              clearable
              :label="t('addSupervisorOrgDialog.email')"
              hide-details="auto"
              variant="outlined"
              :rules="[validEmail]"
            ></v-text-field>
          </v-col>
        </v-row>
        <div class="flex mt-5">
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="loading" @click="showDialog = false">{{
            t("common.close")
          }}</v-btn>
          <v-btn
            type="submit"
            :loading="loading"
            :disabled="loading"
            color="success"
            variant="flat"
            data-testid="add-supervisor-org-dialog-submit-button"
            >{{ t("common.add") }}</v-btn
          >
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import ProAccountPriceOptionsSelect from "@/components/ProAccountPriceOptionsSelect.vue";
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { DEFAULT_PRO_ACCOUNT_PRICE } from "@aircap/common/config";
import { captureException } from "@sentry/vue";
import { ref } from "vue";
import { useFirebaseAuth } from "vuefire";
import { VForm } from "vuetify/components";

const { t } = useI18n();
const auth = useFirebaseAuth();
const toast = useToast();
const { required, validEmail, minLength } = useRules();
const showDialog = defineModel<boolean>();

const email = ref("");
const name = ref("");
const loading = ref(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const proAccountPriceOptions = ref<number[]>([DEFAULT_PRO_ACCOUNT_PRICE]);

const onSubmit = async () => {
  if (!auth) throw new Error("Auth is not defined");

  if (!form.value) return;
  const { valid } = await form.value.validate();
  if (!valid) return;

  try {
    console.log("Adding new supervisorOrg...");
    loading.value = true;
    const { supervisorOrgId } = await call("createSupervisorOrg")({
      name: name.value,
      proAccountPriceOptions: proAccountPriceOptions.value,
    });
    if (email.value) {
      await call("createSupervisorOrgUser")({
        supervisorOrgId,
        email: email.value,
        role: "supervisor",
      });
    }
    form.value.reset();
    form.value.resetValidation();
    showDialog.value = false;
  } catch (cause) {
    const error = new Error("Failed to create supervisor org", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    loading.value = false;
  }
};
</script>
